import React, { useLayoutEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { scroller } from 'react-scroll';

import Accordion, {
  AccordionContent,
  AccordionTitle
} from 'buyside/components/presentation/Accordion/accordionComponents';
import DelayedAccessAlert, {
  Location
} from 'buyside/components/presentation/AccessControl/delayedAccessAlert';
import ShowPageCommentContainer from 'buyside/components/container/ShowPageComments/ShowPageCommentContainer';
import ShowPageCommentForm from 'buyside/components/presentation/ShowPageComments/ShowPageCommentForm/showPageCommentForm';
import ShowPageCommentLoading from 'buyside/components/presentation/ShowPageComments/ShowPageComment/showPageCommentLoading';

import findEventId, { EventType } from 'buyside/util/parseLocationHash';
import { defaultUserImageUrl } from 'buyside/util/urlHelper';

import {
  IdeaAuthorQuery,
  IdeaCoreQuery,
  IdeaCurrentUserQuery,
  Maybe,
  useIdeaCommentsQuery
} from 'interfaces/graphql-typings';

const IdeaAuthor = require('buyside/graphql/queries/ideaShowPage/IdeaAuthor');
const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');
const IdeaCurrentUser = require('buyside/graphql/queries/ideaShowPage/IdeaCurrentUser');

const ShowPageComments: React.FC<{
  authorDisplayName?: Maybe<string>;
  authorTag: string;
  ideaId: string;
  isAuthor: boolean;
  canWriteComments: boolean;
  canRateIdea: boolean;
  loading: boolean;
}> = ({
  authorTag,
  ideaId,
  isAuthor,
  canRateIdea,
  authorDisplayName,
  canWriteComments,
  loading: isLoading
}) => {
  const client = useApolloClient();

  const cachedCurrentUser = client.readQuery<IdeaCurrentUserQuery>({
    query: IdeaCurrentUser,
    variables: { ideaId }
  })?.currentUser;

  const cachedIdeaAuthor = client.readQuery<IdeaAuthorQuery>({
    query: IdeaAuthor,
    variables: { ideaId }
  })?.idea;

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  const { data, loading, error } = useIdeaCommentsQuery({
    variables: { ideaId, controlName: !isAuthor }
  });

  if (error) throw error;

  const { comments, commentsCount, commentsMentionableList } = data?.idea || {};
  const { isNonBuyside, user: ideaAuthor } = cachedIdeaAuthor || {};
  const { anonymous, canCurrentUserAccessIdeaComments, closedAt } = cachedIdea || {};
  const {
    canCommentAnonymously,
    canViewUserDetails,
    id: currentUserId,
    isAdmin,
    isAllocator,
    mostRecentEmployer,
    profileImageUrl
  } = cachedCurrentUser || {};

  const isEmployed = Boolean(mostRecentEmployer?.currentEmployer);
  const isIdeaClosed = Boolean(closedAt);

  const shouldFocus = window.location.hash === '#idea_comments';

  useLayoutEffect(() => {
    if (shouldFocus) {
      scroller.scrollTo('idea_comments', {
        duration: 1000,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
  }, [shouldFocus]);

  return (
    <div id="idea_comments">
      <Accordion basic initialOpen={canCurrentUserAccessIdeaComments}>
        <AccordionTitle
          collapseIcon={faMinus}
          dataTag="comments"
          expandIcon={faPlus}
          expandable={Boolean(canCurrentUserAccessIdeaComments)}
          isLocked={
            canCurrentUserAccessIdeaComments !== undefined && !canCurrentUserAccessIdeaComments
          }
          loading={loading || isLoading}
        >
          Comments {commentsCount ? `(${commentsCount})` : null}
        </AccordionTitle>
        <AccordionContent>
          <>
            {canWriteComments && currentUserId && (
              <ShowPageCommentForm
                authorTag={authorTag}
                canCommentAnonymously={Boolean(canCommentAnonymously)}
                isAllocator={Boolean(isAllocator)}
                ideaId={ideaId}
                isAuthor={isAuthor}
                isIdeaAnonymous={Boolean(anonymous)}
                isEmployed={isEmployed}
                currentUserId={currentUserId}
                mentionables={commentsMentionableList}
                canRateIdea={canRateIdea}
                profileImageUrl={profileImageUrl || null}
              />
            )}
            {(commentsCount || 0) > 0 &&
            Boolean(comments?.edges?.length) &&
            comments?.edges?.length !== commentsCount ? (
              <DelayedAccessAlert id={Location.ideaComments} sectionName="comments" />
            ) : null}
            {loading || isLoading ? (
              [1, 2, 3].map(key => <ShowPageCommentLoading key={key} />)
            ) : (
              <TransitionGroup>
                {comments?.edges
                  ?.filter(edge => !edge?.node?.parentId)
                  .map(edge => (
                    <CSSTransition
                      key={`comment-${edge?.node?.id}`}
                      classNames="fade"
                      timeout={1000}
                      in
                      appear
                    >
                      <ShowPageCommentContainer
                        allComments={comments?.edges?.map(edge => edge?.node || null) || []}
                        authorTag={authorTag}
                        authorId={ideaAuthor?.id || null}
                        authorName={authorDisplayName || null}
                        canCommentAnonymously={Boolean(canCommentAnonymously)}
                        canViewUserDetails={Boolean(canViewUserDetails || isAuthor)}
                        canWriteComments={Boolean(canWriteComments)}
                        comment={edge?.node || null}
                        commentableId={ideaId}
                        commentableType="Idea"
                        currentUserId={currentUserId}
                        highlightedCommentId={findEventId(
                          window.location.hash.slice(1),
                          EventType.Comment
                        )}
                        isAdmin={Boolean(isAdmin)}
                        isAllocator={Boolean(isAllocator)}
                        isAuthor={Boolean(isAuthor)}
                        isEmployed={Boolean(isEmployed)}
                        isIdeaAnonymous={Boolean(anonymous)}
                        isNonBuyside={Boolean(isNonBuyside)}
                        isIdeaClosed={Boolean(isIdeaClosed)}
                        mentionables={commentsMentionableList || []}
                        profileImageUrl={profileImageUrl || defaultUserImageUrl()}
                      />
                    </CSSTransition>
                  ))}
              </TransitionGroup>
            )}
          </>
        </AccordionContent>
      </Accordion>
    </div>
  );
};

export default ShowPageComments;
